import {logger, LongId, Time, Util} from 'yougile-util'
import {
  DataObjectId,
  IndexResp,
  PaymentGetAllRequest,
  PaymentGetAllResponse,
  PaymentGetRequest,
  PaymentGetResponse,
  PaymentStatus
} from 'yougile-model'
import {Backend} from '../../common/backend'
import {globals} from '../extensions/globals-base'
import {Connector} from './connector'
import {SyncEvents} from './sync-events'

export class PaymentClient {
  private static listeners: Map<LongId, (companyId: DataObjectId) => void> =
    new Map()
  private static byCompany: Map<DataObjectId, PaymentStatus> = new Map()
  private static users: Map<DataObjectId, {[userId: string]: boolean}> =
    new Map()
  private static initializedAlready = false

  public static getUsers(companyId: DataObjectId): {[userId: string]: boolean} {
    return PaymentClient.users.get(companyId) ?? {}
  }

  public static isFree(companyId: DataObjectId): boolean {
    if (globals.standalone) {
      return false
    }
    const status = PaymentClient.get(companyId)
    return !status || status.expiration < Time.now()
  }

  public static canAddUsers(companyId: DataObjectId): boolean {
    const users = Object.keys(PaymentClient.users.get(companyId) ?? {}).length
    const licUsers =
      10 +
      (PaymentClient.isFree(companyId)
        ? 0
        : PaymentClient.get(companyId)?.users ?? 0)
    return licUsers > users
  }

  public static isUserDisallowed(
    companyId: DataObjectId,
    userId: DataObjectId
  ): boolean {
    if (globals.standalone) {
      return false
    }
    const all = PaymentClient.users.get(companyId)
    return all && userId in all && !all[userId]
  }

  public static get(companyId: DataObjectId): PaymentStatus {
    if (globals.standalone) {
      return {
        tariff: 'standalone',
        users: 100000,
        expiration: undefined,
        history: [],
        subscriptions: {all: []}
      }
    }
    return this.byCompany.get(companyId)
  }

  public static addListener(l: (companyId: DataObjectId) => void): LongId {
    const id = Util.longId()
    this.listeners.set(id, l)
    return id
  }

  public static init(): void {
    if (globals.standalone) {
      return
    }
    if (this.initializedAlready) {
      return
    }
    this.initializedAlready = true

    this.addListener((companyId: DataObjectId) => {
      SyncEvents.triggerAll(companyId)
      const currentCompany = globals.getCompanyId()
      if (currentCompany !== companyId) {
        globals.setCompanyId(currentCompany)
      }
    })
  }

  public static onConnected(): void {
    void this.fetchAll()
    Connector.onRequest('payment-up', req => {
      void this.fetchCompany(req.companyId)
    })
  }

  public static removeListener(id: LongId): void {
    this.listeners.delete(id)
  }

  public static trigger(companyId: DataObjectId): void {
    setTimeout(() => {
      ;[...this.listeners.values()].forEach(l => Util.catch(() => l(companyId)))
    }, 20)
  }

  public static clear(): void {
    this.byCompany.clear()
    this.users.clear()
  }

  public static fetchAll(): Promise<void> {
    if (globals.standalone) {
      return Promise.resolve()
    }
    if (!Backend.features.payment) {
      logger.log('Billing info not supported')
      return Promise.resolve()
    }
    const req: PaymentGetAllRequest = {
      ...Backend.loadCred()
    }
    return Backend.postJSON<PaymentGetAllResponse>(
      '/data/payment/get-all',
      req
    ).then(res => {
      this.loadAll(res.statuses, res.users)
    })
  }

  public static fetchCompany(companyId: DataObjectId): Promise<void> {
    if (globals.standalone) {
      return Promise.resolve()
    }
    if (!companyId) {
      return Promise.resolve()
    }
    if (!Backend.features.payment) {
      logger.log('Billing info not supported')
      return Promise.resolve()
    }
    const req: PaymentGetRequest = {
      companyId,
      ...Backend.loadCred()
    }
    return Backend.postJSON<PaymentGetResponse>('/data/payment/get', req).then(
      res => {
        this.onCompanyReceived(companyId, res.status, res.users, false)
      }
    )
  }

  public static loadAll(
    statuses: IndexResp['paymentStatuses'],
    users: IndexResp['paymentUsersStatus']
  ): void {
    this.clear()
    if (statuses) {
      Object.keys(statuses).forEach((companyId: DataObjectId) => {
        this.onCompanyReceived(
          companyId,
          statuses[companyId],
          users && users[companyId],
          true
        )
      })
    }
  }

  public static onCompanyReceived(
    companyId: DataObjectId,
    status: PaymentStatus,
    users: {[userId: string]: boolean},
    silent: boolean
  ): void {
    this.byCompany.set(companyId, status)
    this.users.set(companyId, users)
    if (!silent) {
      this.trigger(companyId)
    }
  }

  public static serializaAll(): Pick<
    IndexResp,
    'paymentStatuses' | 'paymentUsersStatus'
  > {
    const paymentStatuses: IndexResp['paymentStatuses'] = {}
    const paymentUsersStatus: IndexResp['paymentUsersStatus'] = {}
    this.byCompany.forEach((v, id) => {
      paymentStatuses[id] = v
    })
    this.users.forEach((v, id) => {
      paymentUsersStatus[id] = v
    })
    return {paymentStatuses, paymentUsersStatus}
  }
}
